// @ts-check
// Configuração de um bloco no painel lateral (contrato de bots, seção 22.2), como nos fluxos: o nome do bloco no topo,
// o formulário do tipo e, no rodapé, "Excluir bloco" e "Fechar". As mudanças valem no rascunho na hora; "Salvar" grava.
import { html } from 'htm/preact';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { MAXIMUM_BOT_NODE_NAME_LENGTH } from '../../../../shared/bots/bot-limits.js';
import { FlowSidePanel } from '../flows/flow-side-panel.js';
import { FlowField } from '../flows/flow-ui.js';
import { BotCategoryCircle, botProblemText, describeBotNode } from './bot-ui.js';
import { MessageForm } from './node-forms/message-form.js';
import { AskForm } from './node-forms/ask-form.js';
import { ConditionForm, EndForm, GotoForm, SetVariableForm, SplitPercentForm, SubbotForm } from './node-forms/path-forms.js';
import { ChooseStudentForm, IdentifyGuardianForm, StrongVerificationForm } from './node-forms/identity-forms.js';
import { HandoffForm, OpenTicketForm, SetPurposeForm } from './node-forms/service-forms.js';
import {
  AddNoteForm, AssignOwnerForm, AutomationBotForm, CreateCaptureCardForm, ScheduleVisitForm, TagsForm, UpdateFieldForm, UpdateStageForm,
} from './node-forms/crm-forms.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/bots/bot-definition.js').BotDefinition} BotDefinition
 * @typedef {import('../../../../shared/bots/bot-definition.js').BotNode} BotNode
 * @typedef {import('../../../../shared/bots/bot-summary.js').BotSummaryLookups} BotSummaryLookups
 * @typedef {import('../../lib/bot-validation.js').BotEditorProblem} BotEditorProblem
 * @typedef {import('./bot-lookups.js').BotLookupData} BotLookupData
 * @typedef {{ node: BotNode, definition: BotDefinition, botId: string, problems: BotEditorProblem[], lookupData: BotLookupData,
 *   lookups: BotSummaryLookups, settings: BotDefinition['settings'], purpose: 'CAPTURE' | 'SERVICE' | 'ANY', variables: string[], institutionName: string,
 *   onConfig: (patch: Record<string, any>) => void, onReplaceConfig: (config: Record<string, any>) => void,
 *   onRequestModeChange?: (mode: 'NONE' | 'BUTTONS' | 'LIST' | 'DYNAMIC') => void, onAddOption?: () => void, onRemoveOption?: (optionId: string) => void,
 *   onGoToNode?: (nodeId: string) => void }} BotNodeFormProps
 *   variables: as variáveis do bot gravadas antes do bloco no caminho (o seletor `{{bot.*}}`)
 */

const FORMS = /** @type {Record<string, (props: BotNodeFormProps) => any>} */ ({
  SEND_MESSAGE: MessageForm,
  ASK: AskForm,
  CONDITION: ConditionForm,
  SPLIT_PERCENT: SplitPercentForm,
  SET_VARIABLE: SetVariableForm,
  GOTO: GotoForm,
  SUBBOT: SubbotForm,
  END: EndForm,
  IDENTIFY_GUARDIAN: IdentifyGuardianForm,
  CHOOSE_STUDENT: ChooseStudentForm,
  STRONG_VERIFICATION: StrongVerificationForm,
  SET_PURPOSE: SetPurposeForm,
  OPEN_TICKET: OpenTicketForm,
  HANDOFF: HandoffForm,
  CREATE_CAPTURE_CARD: CreateCaptureCardForm,
  UPDATE_STAGE: UpdateStageForm,
  UPDATE_FIELD: UpdateFieldForm,
  ADD_TAGS: TagsForm,
  REMOVE_TAGS: TagsForm,
  ASSIGN_OWNER: AssignOwnerForm,
  CREATE_TASK: AutomationBotForm,
  ADD_NOTE: AddNoteForm,
  SEND_NOTIFICATION: AutomationBotForm,
  SCHEDULE_VISIT: ScheduleVisitForm,
  CALL_WEBHOOK: AutomationBotForm,
  HTTP_REQUEST: AutomationBotForm,
});

/** Formulário de cada tipo de bloco (a tela confere que todos têm). */
export const BOT_NODE_FORM_TYPES = Object.freeze(Object.keys(FORMS));

/**
 * @param {Omit<BotNodeFormProps, 'onConfig' | 'onReplaceConfig'> & { onChange: (patch: { name?: string | null, config?: Record<string, any>,
 *   replaceConfig?: Record<string, any> }) => void, onDelete: () => void, onClose: () => void }} props
 */
export function BotNodePanel(props) {
  const { node, problems, lookups, onChange, onDelete, onClose } = props;
  const Form = FORMS[node.type];
  const described = describeBotNode(node, lookups);
  const formProps = /** @type {BotNodeFormProps} */ ({
    ...props,
    onConfig: (patch) => onChange({ config: patch }),
    onReplaceConfig: (config) => onChange({ replaceConfig: config }),
  });
  // pendências sem campo (estrutura, limite, finalidade) ficam no topo do formulário
  const general = problems.filter((problem) => !problem.details?.field && problem.code !== 'NODE_INCOMPLETE');
  return html`<${FlowSidePanel} title=${described.summary.name ?? described.title} help=${t(`bots.panel.help.${node.type}`)} onClose=${onClose}
    panelKey=${node.id} testId="bot-node-panel" backLabel=${t('bots.panel.backToBot')}
    headerExtra=${html`<${BotCategoryCircle} type=${node.type} size=${24} />`}
    footer=${html`<button type="button" class="flow-danger-link" onClick=${onDelete}><${Icon} name="IconTrash" size=${14} /><span>${t('bots.panel.deleteNode')}</span></button>
      <${Button} title=${t('bots.panel.close')} variant="secondary" size="small" onClick=${onClose} />`}>
    <${FlowField} label=${t('bots.panel.nodeName')} htmlFor=${`bot-node-name-${node.id}`} hint=${t('bots.panel.nodeNameHint')}>
      <input id=${`bot-node-name-${node.id}`} class="text-input" value=${node.name ?? ''} maxLength=${MAXIMUM_BOT_NODE_NAME_LENGTH}
        placeholder=${t(`bots.nodes.${node.type}.name`)} onInput=${(/** @type {InputEvent} */ event) => onChange({ name: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
    <//>
    ${general.map((problem) => html`<p key=${`${problem.code}-${problem.details?.variable ?? ''}`} class=${problem.severity === 'ERROR' ? 'flow-field-error' : 'bot-field-warning'}
      role="alert">${botProblemText(problem)}</p>`)}
    ${Form ? html`<${Form} key=${node.id} ...${formProps} />` : html`<p class="flow-field-hint">${t('bots.panel.unknownType')}</p>`}
  <//>`;
}
